import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Card, CardHeader, ErrorState, Icon, PageHeader, PageLoader, useToast } from '../../../components/ui';
import { useCrumb } from '../../../components/layout/Breadcrumbs/Breadcrumbs';
import { KeyValues, ReasonDialog, StatusBadge, TotalsBox, TwoColumn } from '../../../components/common/Parts/Parts';
import { PartyCard } from '../../../components/common/PartyCard/PartyCard';
import { useAction } from '../../../hooks/useAction';
import { useFetch } from '../../../hooks/useFetch';
import { labelOf } from '../../../utils/labels';
import { PAYMENT_MODES } from '../../../constants/payments';
import { PAYMENT_STATUS, RECORD_STATUS } from '../../../constants/status';
import { formatDate } from '../../../utils/date';
import { money } from '../../../utils/currency';
import { viewUrl } from '../../../services/api/uploadService';
import { purchaseService } from '../services/purchaseService';

export default function PurchaseDetails() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { busy, run } = useAction(toast);
  const [cancelling, setCancelling] = useState(false);
  const { data: p, error, reload } = useFetch(() => purchaseService.get(id), [id]);
  const { data: payments } = useFetch(() => purchaseService.payments(id), [id, p?.paidAmount]);
  useCrumb(`/purchases/${id}`, p?.purchaseNo);

  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!p) return <PageLoader />;

  const active = p.status === 'active';
  const cancel = async (reason) => {
    const res = await run('cancel', () => purchaseService.cancel(id, reason), 'Purchase cancelled and stock removed');
    setCancelling(false);
    if (res) reload();
  };

  return (
    <>
      <PageHeader
        title={p.purchaseNo}
        badge={<span className="flex gap-2"><StatusBadge map={RECORD_STATUS} value={p.status} />{active && <StatusBadge map={PAYMENT_STATUS} value={p.paymentStatus} />}</span>}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <span className="inline-flex items-center gap-1.5"><Icon name="truck" size={15} />{p.vendor?.name}</span>
            {p.vendorBillNo && <span className="inline-flex items-center gap-1.5"><Icon name="receipt" size={15} />Bill {p.vendorBillNo}</span>}
            <span className="inline-flex items-center gap-1.5"><Icon name="calendar" size={15} />{formatDate(p.billDate)}</span>
          </span>
        }
        actions={
          <>
            {active && <Button variant="dangerText" onClick={() => setCancelling(true)}>Cancel purchase</Button>}
            {p.billPhotoUrl && <Button variant="outline" leftIcon="eye" loading={busy === 'bill'} onClick={() => run('bill', async () => window.open(await viewUrl(p.billPhotoUrl as string), '_blank', 'noopener'))}>View bill</Button>}
            {active && <Button variant="outline" leftIcon="edit" onClick={() => navigate(`/purchases/${id}/edit`)}>Edit details</Button>}
            {active && p.balanceAmount > 0 && <Button leftIcon="rupee" onClick={() => navigate(`/transactions/new?category=purchase_payment&purchaseId=${id}`)}>Pay supplier</Button>}
          </>
        }
      />
      {!active && <p className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">Cancelled: {p.cancelReason}</p>}

      <TwoColumn
        main={
          <>
            <Card className="overflow-hidden">
              <CardHeader title="Materials" subtitle={p.purpose === 'for_service' ? 'Bought for a job and issued to it' : 'Added to stock'} className="pb-4" />
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-left text-sm">
                  <thead className="border-y border-line text-[13px] text-ink-500">
                    <tr>
                      <th scope="col" className="px-5 py-2.5 font-normal">Material</th>
                      <th scope="col" className="px-5 py-2.5 text-right font-normal">Qty</th>
                      <th scope="col" className="px-5 py-2.5 text-right font-normal">Rate</th>
                      {p.isGst && <th scope="col" className="px-5 py-2.5 text-right font-normal">GST</th>}
                      <th scope="col" className="px-5 py-2.5 text-right font-normal">Cost / unit</th>
                      <th scope="col" className="px-5 py-2.5 text-right font-normal">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="tabular divide-y divide-line">
                    {p.items.map((l) => (
                      <tr key={l._id}>
                        <td className="px-5 py-3"><Link to={`/materials/${l.itemId}`} className="font-medium text-navy-900 hover:text-brand-600">{l.name}</Link></td>
                        <td className="whitespace-nowrap px-5 py-3 text-right">{l.qty} {l.unit}</td>
                        <td className="px-5 py-3 text-right">{money(l.rate)}</td>
                        {p.isGst && <td className="px-5 py-3 text-right text-ink-500">{l.gstRate}%</td>}
                        <td className="px-5 py-3 text-right text-ink-500">{money(l.costRate)}</td>
                        <td className="px-5 py-3 text-right font-semibold">{money(l.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="border-t border-line p-5 sm:ml-auto sm:max-w-sm">
                <TotalsBox totals={p} isGst={p.isGst} label="Bill total" />
              </div>
            </Card>
            {p.notes && <Card className="p-5 text-sm text-ink-600"><p className="mb-1 text-[13px] text-ink-400">Notes</p>{p.notes}</Card>}
          </>
        }
        aside={
          <>
            <Card>
              <CardHeader title="Payment" />
              <div className="p-5 pt-4">
                <KeyValues
                  rows={[
                    ['Bill total', money(p.grandTotal), { strong: true }],
                    ['Paid', money(p.paidAmount)],
                    ['Balance', money(p.balanceAmount), { strong: true, tone: p.balanceAmount > 0 ? 'text-red-600' : undefined }],
                    p.serviceId && ['For job', <Link to={`/jobs/${p.serviceId}`} className="text-brand-600 hover:underline">Open job</Link>],
                  ]}
                />
              </div>
              {payments && payments.length > 0 && (
                <ul className="divide-y divide-line border-t border-line">
                  {payments.map((t) => (
                    <li key={t._id}>
                      <Link to={`/transactions/${t._id}`} className="flex justify-between gap-3 px-5 py-2.5 text-sm hover:bg-navy-50/60">
                        <span>{formatDate(t.date)} · {labelOf(PAYMENT_MODES, t.mode)}</span>
                        <span className={`tabular font-semibold ${t.isReversed ? 'line-through opacity-60' : ''}`}>{money(t.amount)}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <PartyCard title="Supplier" party={p.vendor} to={`/vendors/${p.vendorId}`} />
          </>
        }
      />
      <ReasonDialog
        open={cancelling}
        onClose={() => setCancelling(false)}
        onConfirm={cancel}
        loading={busy === 'cancel'}
        title="Cancel this purchase?"
        message="Its stock is taken back out. Purchases with payments must have them reversed first."
        confirmLabel="Cancel purchase"
      />
    </>
  );
}
